<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="5">
          <FormulateInput
            id="tax-configuration-group-name"
            v-model="filters.name"
            type="text"
            :label="$t('Nome')"
            :placeholder="$t('Digite para pesquisar')"
            autofocus
          />
        </b-col>
        <b-col md="5">
          <FormulateInput
            id="tax-configuration-group-ncm"
            v-model="filters.ncm"
            type="select-searchable"
            :label="$t('NCM')"
            :placeholder="$t('Digite para pesquisar')"
            :options="getComboNcms"
            @fetchSearch="ncmSearch"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="tax-configuration-group-active"
            v-model="filters.active"
            type="vue-select"
            :label="$t('Status')"
            :options="statusOptions"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="getData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="taxConfigurationGroups-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('TAX_CONFIGURATION_GROUP.NAME'), 'female')"
        :fields="fields"
        :items="taxConfigurationGroups"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(action)="row">
          <e-grid-actions
            :show-update="$can('Update', 'TaxConfigurationGroup')"
            :show-delete="$can('Delete', 'TaxConfigurationGroup')"
            :buttons="gridActionButtons"
            @update="updateTaxConfigurationGroup(row.item)"
            @delete="deleteTaxConfigurationGroup(row.item)"
            @associate-skus="openSkuModal(row.item)"
          />
        </template>

        <template #cell(skuCount)="{ item }">
          <b-link
            v-if="$can('Update', 'TaxConfigurationGroup')"
            :title="$t('Associar produtos')"
            @click="openSkuModal(item)"
          >
            {{ item.skuCount }}
          </b-link>
          <span v-else>{{ item.skuCount }}</span>
        </template>

        <template #cell(active)="{ item }">
          <e-status-badge :status="item.active" />
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="taxConfigurationGroups-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <FAB
      v-if="$can('Create', 'TaxConfigurationGroup')"
      :main-tooltip="$t('Adicionar Exceção Fiscal')"
      @click="create"
    />

    <tax-configuration-group-sku-modal
      ref="skuModal"
      @saved="getData"
    />
  </div>
</template>

<script>
import { BTable, BPagination, BRow, BCol, BLink } from 'bootstrap-vue'
import {
  FAB,
  EPageSizeSelector,
  EPaginationSummary,
  EGridActions,
  EStatusBadge,
} from '@/views/components'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { stringUtils } from '@/mixins'
import { mapGetters, mapActions, mapState } from 'vuex'
import EFilters from '@/views/components/EFilters.vue'
import TaxConfigurationGroupSkuModal from './components/TaxConfigurationGroupSkuModal.vue'

export default {
  name: 'TaxConfigurationGroupList',
  components: {
    BTable,
    BPagination,
    BRow,
    BCol,
    BLink,
    BCardActions,
    EPageSizeSelector,
    EPaginationSummary,
    EGridActions,
    EStatusBadge,
    FAB,
    EFilters,
    TaxConfigurationGroupSkuModal,
  },
  mixins: [stringUtils],
  data() {
    return {
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/invoice/taxConfigurationGroups', [
      'taxConfigurationGroups',
      'paging',
      'sorting',
      'filters',
    ]),
    ...mapGetters('common/ncms', ['getComboNcms']),

    statusOptions() {
      return [
        { value: true, label: this.$t('Ativo') },
        { value: false, label: this.$t('Inativo') },
      ]
    },

    gridActionButtons() {
      if (!this.$can('Update', 'TaxConfigurationGroup')) return []

      return [
        {
          icon: 'box-seam',
          variant: 'primary',
          title: this.$t('Associar produtos'),
          event: 'associate-skus',
        },
      ]
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        { label: this.$t('Nome'), key: 'name', sortable: true },
        { label: this.$t('Descrição'), key: 'description' },
        {
          label: this.$t('NCM'),
          key: 'ncm',
          class: 'text-center',
          formatter: value => value || this.$t('Todos'),
        },
        { label: this.$t('Exceções'), key: 'itemCount', class: 'text-center' },
        { label: this.$t('Produtos'), key: 'skuCount', class: 'text-center' },
        { label: this.$t('Status'), key: 'active', class: 'text-center' },
      ]
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    ...mapActions('common/ncms', ['fetchNcms']),
    ...mapActions('pages/invoice/taxConfigurationGroups', [
      'fetchTaxConfigurationGroups',
      'setPageSize',
      'setCurrentPage',
      'resetFilters',
    ]),

    async ncmSearch(loading, search) {
      try {
        loading(true)
        await this.fetchNcms({ itemSearch: search })
      } catch (e) {
        this.showError({ message: this.$t('Não foi possível fazer a pesquisa') })
      } finally {
        loading(false)
      }
    },

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchTaxConfigurationGroups()
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },
    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },
    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },
    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    create() {
      this.$router.push({ name: 'tax-configuration-group-add' })
    },

    openSkuModal(taxConfigurationGroup) {
      if (!this.$can('Update', 'TaxConfigurationGroup')) return

      this.$refs.skuModal.show(taxConfigurationGroup)
    },

    updateTaxConfigurationGroup(taxConfigurationGroup) {
      this.$router.push({
        name: 'tax-configuration-group-maintain',
        params: { id: taxConfigurationGroup.id },
      })
    },

    /**
     * Um cadastro associado a produtos não pode ser excluído: eles voltariam a resolver pela
     * classificação fiscal do NCM. A API recusa, e aqui a tela diz antes qual é o passo que
     * falta — retirar as associações pela ação "Associar produtos".
     */
    async deleteTaxConfigurationGroup(taxConfigurationGroup) {
      if (taxConfigurationGroup.skuCount > 0) {
        this.showWarning({
          message: this.$t('TAX_CONFIGURATION_GROUP.VALIDATION.DELETE_IN_USE', {
            name: taxConfigurationGroup.name,
            count: taxConfigurationGroup.skuCount,
          }),
        })
        return
      }

      try {
        const confirmed = await this.confirm({
          text: this.$t('TAX_CONFIGURATION_GROUP.DELETE_CONFIRM', {
            name: taxConfigurationGroup.name,
          }),
        })

        if (!confirmed) return

        await this.$http.delete(`/api/tax/tax-configuration-groups/${taxConfigurationGroup.id}`)
        this.getData()
        this.showSuccess({ message: this.$t('Exceção fiscal excluída com sucesso') })
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>
